// File: /var/www/app2/frontend/src/components/UserManagement.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";

// Interface for form data
interface UserFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: "supporter" | "admin";
  organizationId: string;
  active: boolean;
}

// Interface for backend user data
interface BackendUser {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  role: "supporter" | "admin";
  organization_id: string | null;
  organization?: string;
  active: boolean;
}

// Interface for organization data
interface Organization {
  id: number | string;
  name: string;
}

interface UserManagementProps {
  user: User;
}

function UserManagement({ user }: UserManagementProps): React.ReactElement {
  const [users, setUsers] = useState<BackendUser[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<BackendUser | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Form data
  const [formData, setFormData] = useState<UserFormData>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "supporter",
    organizationId: "",
    active: true,
  });

  useEffect(() => {
    if (user.role === "admin" || user.role === "superadmin") {
      fetchUsers();
      fetchOrganizations();
    }
  }, [user]);

  const fetchUsers = async (): Promise<void> => {
    try {
      const response = await axios.get<{ users: BackendUser[] }>("/api/users");
      setUsers(response.data.users || []);
      setError("");
    } catch (error: any) {
      console.error("Failed to fetch users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async (): Promise<void> => {
    try {
      const response = await axios.get<{ organizations: Organization[] }>(
        "/api/organizations"
      );
      setOrganizations(response.data.organizations || []);
    } catch (error: any) {
      console.error("Failed to fetch organizations:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    const target = e.target as HTMLInputElement & HTMLSelectElement;
    const { name, value, type, checked } = target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const newRole = e.target.value as "supporter" | "admin";
    // Find MidIndia organization ID for admins
    const midIndiaOrg = organizations.find((org) => org.name === "MidIndia");
    setFormData({
      ...formData,
      role: newRole,
      // Auto-set organization for admins
      organizationId:
        newRole === "admin" && midIndiaOrg
          ? String(midIndiaOrg.id)
          : formData.organizationId,
    });
  };

  const resetForm = (): void => {
    setFormData({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "supporter",
      organizationId: "",
      active: true,
    });
    setEditingUser(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!editingUser && formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!editingUser && formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    // Check organization requirement for supporters
    if (formData.role === "supporter" && !formData.organizationId) {
      setError("Organization is required for supporter role");
      return;
    }

    try {
      const userData: Record<string, any> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        role: formData.role,
        organizationId: formData.organizationId || null,
        active: formData.active,
      };

      if (!editingUser) {
        // Create new user
        userData.password = formData.password;
        await axios.post("/api/users", userData);
        setSuccess("User created successfully!");
      } else {
        // Update existing user
        if (formData.password) {
          userData.password = formData.password;
        }
        await axios.put(`/api/users/${editingUser.id}`, userData);
        setSuccess("User updated successfully!");
      }

      resetForm();
      fetchUsers();
    } catch (error: any) {
      // Show detailed validation errors if available
      const details = error.response?.data?.details;
      if (details && Array.isArray(details) && details.length > 0) {
        const messages = details.map((d: any) => d.msg).join("; ");
        setError(messages);
      } else {
        setError(error.response?.data?.error || "Failed to save user");
      }
    }
  };

  const handleEdit = (userToEdit: BackendUser): void => {
    setFormData({
      firstName: userToEdit.first_name || "",
      lastName: userToEdit.last_name || "",
      username: userToEdit.username,
      email: userToEdit.email || "",
      password: "",
      confirmPassword: "",
      role: userToEdit.role,
      organizationId: userToEdit.organization_id || "",
      active: userToEdit.active,
    });
    setEditingUser(userToEdit);
    setShowAddForm(true);
    setError("");
    setSuccess("");
  };

  const handleToggleActive = async (
    userId: number,
    currentStatus: boolean
  ): Promise<void> => {
    try {
      await axios.patch(`/api/users/${userId}/toggle-active`, {
        active: !currentStatus,
      });
      fetchUsers();
      setSuccess("User status updated");
    } catch (error: any) {
      setError("Failed to update user status");
    }
  };

  const handleDelete = async (userId: number, username: string): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete user ${username}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/users/${userId}`);
      setSuccess("User deleted successfully");
      fetchUsers();
    } catch (error: any) {
      setError("Failed to delete user");
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(
    (u) =>
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user.role !== "admin" && user.role !== "superadmin") {
    return (
      <div style={{ padding: "40px", color: "white", textAlign: "center" }}>
        <h2>Access Denied</h2>
        <p>Only administrators can manage users.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ color: "white", marginBottom: "30px" }}>User Management</h2>

      {/* Add/Edit User Form */}
      {showAddForm && (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "25px",
            marginBottom: "30px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3 style={{ marginBottom: "20px", color: "#2d3748" }}>
            {editingUser ? "Edit User" : "Add New User"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={editingUser ? true : false}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: editingUser ? "#f7fafc" : "white",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Password {!editingUser && "*"}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder={
                    editingUser ? "Leave blank to keep current password" : ""
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Confirm Password {!editingUser && "*"}
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!editingUser}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <option value="supporter">Supporter</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Organization {formData.role === "supporter" && "*"}
                </label>
                <select
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleInputChange}
                  required={formData.role === "supporter"}
                  disabled={formData.role === "admin"}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor:
                      formData.role === "admin" ? "#f7fafc" : "white",
                  }}
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {organizations.length === 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#e53e3e",
                      marginTop: "5px",
                    }}
                  >
                    No organizations available. Please create one first in the
                    Organizations page.
                  </div>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  style={{ marginRight: "8px" }}
                />
                <label>Active</label>
              </div>
            </div>

            {error && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#fed7d7",
                  color: "#c53030",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#c6f6d5",
                  color: "#276749",
                  borderRadius: "4px",
                }}
              >
                {success}
              </div>
            )}

            <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#48bb78",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {editingUser ? "Update User" : "Create User"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#718096",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Bar */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4299e1",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          + Add New User
        </button>

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          style={{
            padding: "8px 12px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            width: "300px",
          }}
        />
      </div>

      {/* Users Table */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                backgroundColor: "#f7fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <th style={{ padding: "12px", textAlign: "left" }}>Name</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Username</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Email</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Role</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Organization
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#718096",
                  }}
                >
                  {searchTerm
                    ? "No users found matching your search"
                    : "No users found"}
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "12px" }}>
                    {u.first_name} {u.last_name}
                  </td>
                  <td style={{ padding: "12px" }}>{u.username}</td>
                  <td style={{ padding: "12px" }}>{u.email}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        backgroundColor:
                          u.role === "admin" ? "#805ad5" : "#4299e1",
                        color: "white",
                      }}
                    >
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>{u.organization}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor: u.active ? "#c6f6d5" : "#fed7d7",
                        color: u.active ? "#276749" : "#c53030",
                      }}
                    >
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <button
                      onClick={() => handleEdit(u)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#4299e1",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "5px",
                        fontSize: "14px",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(u.id, u.active)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: u.active ? "#ed8936" : "#48bb78",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "5px",
                        fontSize: "14px",
                      }}
                    >
                      {u.active ? "Deactivate" : "Activate"}
                    </button>
                    {u.username !== user.username && (
                      <button
                        onClick={() => handleDelete(u.id, u.username)}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#f56565",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: "20px",
          color: "white",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        Total Users: {filteredUsers.length} | Admins:{" "}
        {filteredUsers.filter((u) => u.role === "admin").length} | Supporters:{" "}
        {filteredUsers.filter((u) => u.role === "supporter").length}
      </div>
    </div>
  );
}

export default UserManagement;
